import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HeaderNav } from "@/components/HeaderNav";
import { GlimpseMap } from "@/components/glimpse/GlimpseMap";
import { GlimpseExperiment } from "@/components/glimpse/GlimpseExperiment";
import { GlimpseShare } from "@/components/glimpse/GlimpseShare";
import { GlimpseJourneyCard } from "@/components/glimpse/GlimpseJourneyCard";
import { GlimpseReminderOptIn } from "@/components/glimpse/GlimpseReminderOptIn";
import { GlimpseFeedback } from "@/components/glimpse/GlimpseFeedback";
import { useSettings } from "@/context/SettingsContext";
import { useAuth } from "@/context/AuthContext";
import { SharePopup } from "@/components/share/SharePopup";
import { SevenDotsLine, ConnectsRow } from "@/components/DimDots";
import api from "@/lib/api";

const Invitation = () => {
  const navigate = useNavigate();
  return (
    <div className="cg-card p-7 sm:p-8" data-testid="glimpse-full-invitation">
      <h2 className="font-serif text-3xl text-[#F3F4F6]">This Is Just the Beginning</h2>
      <p className="text-[#B9BDC7] mt-3">Your Authority Glimpse offers an introduction to seven dimensions of personal authority.</p>
      <p className="text-[#B9BDC7] mt-2">The full Human Authority Assessment explores these dimensions in greater depth through 105 statements and provides a more comprehensive Authority Portrait.</p>
      <button data-testid="glimpse-explore-full" onClick={() => navigate("/payment")}
        className="mt-6 bg-brass text-obsidian font-medium px-6 py-3 rounded-full cg-scale-btn hover:bg-amber2">Explore My Full Authority Portrait</button>
    </div>
  );
};

export default function GlimpsePortrait() {
  const navigate = useNavigate();
  const { brand } = useSettings();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/glimpse/portrait").then((r) => setData(r.data)).catch(() => navigate("/glimpse", { replace: true }));
  }, [navigate]);
  if (!data) return <div><HeaderNav /><div className="max-w-3xl mx-auto px-5 pt-24 cg-eyebrow animate-pulse">Loading…</div></div>;
  const owl = brand?.logo_dark || brand?.logo_primary;
  return (
    <div>
      <HeaderNav />
      <div className="max-w-3xl mx-auto px-5 sm:px-8 pt-12 pb-24 space-y-8" data-testid="glimpse-portrait">
        <div className="cg-fade-up">
          {owl && <img src={owl} alt="Caesar's Ghost" className="h-20 w-auto mb-5" />}
          <p className="cg-eyebrow mb-3" data-testid="glimpse-label">Authority Glimpse · introductory reflection</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#F3F4F6]">Your Authority Glimpse</h1>
          <SevenDotsLine testId="glimpse-seven-dots" />
          <p className="font-serif text-2xl text-brass mt-4" data-testid="glimpse-welcome">Welcome, {data.first_name}.</p>
          <div className="mt-4 space-y-2 text-[#B9BDC7] leading-relaxed">
            <p>This is a first look at some of the influences shaping your relationship with personal authority.</p>
            <p>Your portrait reflects how you responded to 21 statements across seven dimensions.</p>
            <p>It is an invitation to reflect, not a label or a judgement.</p>
          </div>
          <p className="mt-4 text-xs text-[#6B7280]" data-testid="glimpse-disclaimer">An introductory reflection based on three statements per dimension. It is not a comprehensive or psychometrically validated result, uses provisional reflection ranges, and is not comparable with the full 105-statement Authority Portrait.</p>
        </div>
        <div className="cg-card p-7 sm:p-8"><GlimpseMap dimensions={data.dimensions} />
          <ConnectsRow title="How your Glimpse connects" testId="glimpse-connects" steps={["21 statements", "Your seven dimensions", "Your reflections", "Full Authority Portrait"]} />
        </div>
        <div className="cg-card p-7 sm:p-8" data-testid="glimpse-reflection">
          <h2 className="font-serif text-2xl text-[#F3F4F6] mb-3">Questions to Reflect On</h2>
          {data.reflection_prompts.map((q) => <p key={q} className="font-serif text-xl text-[#E8EAEE] mt-2">“{q}”</p>)}
        </div>
        <Invitation />
        <GlimpseExperiment />
        <GlimpseJourneyCard journey={data.journey} variant="portrait" />
        <GlimpseReminderOptIn reminder={data.reminder} journey={data.journey} />
        <GlimpseShare />
        <GlimpseFeedback statements={data.statements} submitted={data.feedback_submitted} />
      </div>
      <SharePopup userId={user?.id} portraitKey={data.completed_at} source="glimpse" />
    </div>
  );
}
